'use client';

import React, { useContext } from 'react';
import type { ElementType } from 'react';
import type { SeparatorProps as AriaSeparatorProps } from 'react-aria-components';
import { Separator as AriaSeparator } from 'react-aria-components';
import { cx } from '../../../utils/cx';
import type { PolymorphicProps } from '../../../types/polymorphic';
import { MenuContext } from './menuContext';
import { styles } from './menuItemStyles';
import type { MenuSize } from './menuTypes';

const MENU_SEPARATOR_DISPLAY_NAME = 'RomaMenuSeparator';

type MenuSeparatorOwnProps = {
    size?: MenuSize;
    className?: string;
};

type MenuSeparatorBaseComponentProps<T extends ElementType> = PolymorphicProps<MenuSeparatorOwnProps, T>;

export type MenuSeparatorBaseProps = PolymorphicProps<MenuSeparatorOwnProps>;

export const MenuSeparatorBase = <T extends ElementType = 'div'>(props: MenuSeparatorBaseComponentProps<T>) => {
    const { as, size: sizeProp, className, ...restProps } = props;
    const { size: contextSize } = useContext(MenuContext);
    const size = sizeProp ?? contextSize;
    const Component = as ?? 'div';

    return (
        <Component
            {...restProps}
            className={cx(
                MENU_SEPARATOR_DISPLAY_NAME,
                styles.common.separator,
                styles.sizes[size].separator,
                className,
            )}
        />
    );
};

export type MenuSeparatorProps = Omit<AriaSeparatorProps, 'className' | 'orientation'> & {
    size?: MenuSize;
    className?: string;
};

export const MenuSeparator: React.FC<MenuSeparatorProps> = ({ className, size, ...props }) => (
    <MenuSeparatorBase as={AriaSeparator} {...props} size={size} orientation="horizontal" className={className} />
);

MenuSeparatorBase.displayName = MENU_SEPARATOR_DISPLAY_NAME;
MenuSeparator.displayName = MENU_SEPARATOR_DISPLAY_NAME;
